import { HttpClient } from '@angular/common/http';
import { Signal, WritableSignal, computed, signal } from '@angular/core';
import { Observable, finalize, map, share, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { joinUrl } from '../http/url.util';

/** The token pair every rekruto login and refresh response carries (B1/C5 wire shape). */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * The refresh endpoint's raw response: the rotated pair plus optional identity members (the
 * company refresh carries the role; the candidate refresh carries nothing but the pair).
 */
export interface RefreshWireResult {
  accessToken: string;
  refreshToken: string;
  [key: string]: unknown;
}

/** Extra persisted identity fields a service exposes as signals (email, role, ids). */
export type AuthIdentity = Record<string, string | null>;

export interface AuthEngineConfig {
  /** localStorage key prefix, e.g. `rekruto` -> `rekruto_access_token`. */
  storagePrefix: string;
  /** Refresh endpoint path relative to the API base, e.g. `auth/refresh`. */
  refreshPath: string;
  /** Revocation endpoint path relative to the API base, e.g. `auth/logout`. */
  logoutPath: string;
  /** The identity fields the owning service persists and exposes. */
  identityKeys: readonly string[];
  /** Identity fields to re-adopt from a refresh response (e.g. the rotated role). */
  identityFromRefresh?: (result: RefreshWireResult) => AuthIdentity;
  /**
   * Endpoint paths (relative to the API base) a caller can hit while deliberately
   * unauthenticated. A 401 from one of these is an expected outcome of the call itself - bad
   * credentials, unknown/expired token - not a sign the caller's own session expired, so it must
   * reach the calling screen instead of triggering refresh/sign-out.
   */
  unauthenticatedPaths: readonly string[];
}

/**
 * The one JWT + refresh-token engine behind both rekruto auth schemes. `AuthService` (company
 * scheme, `auth/*` endpoints) and `CandidateAuthService` (candidate scheme, audience `candidate`,
 * `candidate/*` endpoints) each own a private instance and share this machinery - token pair
 * storage with rotation, the single in-flight refresh, and the idempotent server-side revoke.
 *
 * A plain class, not an Injectable: each owning service constructs it with its own config and
 * injected dependencies, so there is exactly one engine per scheme and the two schemes can never
 * cross-read each other's storage keys.
 */
export class AuthSessionEngine {
  readonly #config: AuthEngineConfig;
  readonly #http: HttpClient;
  readonly #apiBaseUrl: string;
  readonly #router: Router;
  readonly #loginRoute: string[];

  readonly #accessToken: WritableSignal<string | null>;
  readonly #refreshToken: WritableSignal<string | null>;
  readonly #identity: Record<string, WritableSignal<string | null>> = {};

  /** The one in-flight refresh per engine; concurrent 401s await it instead of minting their own. */
  #refreshInFlight: Observable<AuthTokens> | null = null;

  readonly accessToken: Signal<string | null>;
  readonly isAuthenticated: Signal<boolean>;

  constructor(config: AuthEngineConfig, http: HttpClient, apiBaseUrl: string, router: Router, loginRoute: string[]) {
    this.#config = config;
    this.#http = http;
    this.#apiBaseUrl = apiBaseUrl;
    this.#router = router;
    this.#loginRoute = loginRoute;

    this.#accessToken = signal(localStorage.getItem(this.#storageKey('access_token')));
    this.#refreshToken = signal(localStorage.getItem(this.#storageKey('refresh_token')));
    for (const key of config.identityKeys) {
      this.#identity[key] = signal(localStorage.getItem(this.#storageKey(key)));
    }

    this.accessToken = this.#accessToken.asReadonly();
    this.isAuthenticated = computed(() => !!this.#accessToken());
  }

  identityValue(key: string): Signal<string | null> {
    const identitySignal = this.#identity[key];
    if (!identitySignal) {
      throw new Error(`Unknown identity key "${key}" for the "${this.#config.storagePrefix}" auth scheme.`);
    }
    return identitySignal.asReadonly();
  }

  /** Adopts a full session minted by a login response: the pair plus every identity field. */
  adoptSession(tokens: AuthTokens, identity: AuthIdentity): void {
    this.#accessToken.set(tokens.accessToken);
    this.#refreshToken.set(tokens.refreshToken);
    localStorage.setItem(this.#storageKey('access_token'), tokens.accessToken);
    localStorage.setItem(this.#storageKey('refresh_token'), tokens.refreshToken);
    this.patchIdentity(identity);
  }

  /** Merges the given identity keys into storage; null removes the key. */
  patchIdentity(partial: AuthIdentity): void {
    for (const [key, value] of Object.entries(partial)) {
      const identitySignal = this.#identity[key];
      if (!identitySignal) continue;
      identitySignal.set(value);
      if (value === null) {
        localStorage.removeItem(this.#storageKey(key));
      } else {
        localStorage.setItem(this.#storageKey(key), value);
      }
    }
  }

  /** Drops the whole session from signals and storage. Does not navigate. */
  clearSession(): void {
    this.#accessToken.set(null);
    this.#refreshToken.set(null);
    localStorage.removeItem(this.#storageKey('access_token'));
    localStorage.removeItem(this.#storageKey('refresh_token'));
    for (const key of this.#config.identityKeys) {
      this.#identity[key].set(null);
      localStorage.removeItem(this.#storageKey(key));
    }
  }

  /**
   * Rotates the token pair from a refresh response, re-adopting whatever identity the configured
   * `identityFromRefresh` maps out of it. Returns the new pair.
   */
  refresh(): Observable<AuthTokens> {
    const current = this.#refreshToken();
    if (!current) {
      return throwError(() => new Error('No refresh token.'));
    }
    if (this.#refreshInFlight !== null) {
      return this.#refreshInFlight;
    }

    const request = this.#http
      .post<RefreshWireResult>(joinUrl(this.#apiBaseUrl, this.#config.refreshPath), { refreshToken: current })
      .pipe(
        map(result => {
          this.#accessToken.set(result.accessToken);
          this.#refreshToken.set(result.refreshToken);
          localStorage.setItem(this.#storageKey('access_token'), result.accessToken);
          localStorage.setItem(this.#storageKey('refresh_token'), result.refreshToken);
          this.patchIdentity(this.#config.identityFromRefresh?.(result) ?? {});
          return { accessToken: result.accessToken, refreshToken: result.refreshToken };
        }),
        finalize(() => {
          this.#refreshInFlight = null;
        }),
        share(),
      );
    this.#refreshInFlight = request;
    return request;
  }

  /**
   * Signs out: revokes the server-side refresh grant (the endpoint kills the stored grant so no
   * new pair can be minted), then clears local state and sends the visitor to the login route.
   *
   * The order is load-bearing. The revoke POST is dispatched BEFORE `clearSession` so the
   * interceptor attaches the still-valid bearer; local teardown happens immediately after, so a
   * dead network can never trap the user in the app. It is also idempotent: a re-entrant call
   * (the auth interceptor's refresh-failure path) sees no token and stops, so the revoke POST can
   * never recurse into another logout. The revoke result is deliberately not surfaced - local
   * sign-out already happened, and a failed revoke leaves the refresh grant to expire on its own.
   */
  logout(): void {
    const hasSession = this.#accessToken() !== null;
    if (hasSession) {
      // The revoke must carry the bearer itself: the owning app may wire no interceptor at all,
      // and the revoke is an engine concern, not an interceptor concern.
      const token = this.#accessToken();
      this.#http
        .post(joinUrl(this.#apiBaseUrl, this.#config.logoutPath), {}, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .subscribe({ error: () => undefined });
    }
    this.clearSession();
    void this.#router.navigate(this.#loginRoute);
  }

  unauthenticatedPaths(): readonly string[] {
    return this.#config.unauthenticatedPaths;
  }

  #storageKey(key: string): string {
    return `${this.#config.storagePrefix}_${key}`;
  }
}